import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ImageLightbox from '../components/ui/ImageLightbox';
import { useDebounce } from '../hooks/useDebounce';

const ITEMS_PER_PAGE = 80;

const getImageSrc = (product) => {
  const image = product.images?.[0];
  if (!image) return null;
  return image.startsWith('http') ? image : `/api${image}`;
};

const compareSku = (a, b) => {
  const skuA = a.sku || '';
  const skuB = b.sku || '';

  if (!skuA && skuB) return 1;
  if (skuA && !skuB) return -1;

  return skuA.localeCompare(skuB, 'he', { numeric: true, sensitivity: 'base' });
};

const getBrandQuery = (brandKey) => {
  if (!brandKey) return {};

  if (brandKey.startsWith('id:')) {
    return { brand_id: brandKey.slice(3) };
  }

  if (brandKey.startsWith('legacy:')) {
    return { brand: brandKey.slice(7) };
  }

  return {};
};

/*
 * Clean storefront design (inspired by nala-market): white canvas,
 * soft gray product tiles, rounded cards, centered product info,
 * big brand tiles for one-tap brand selection. Scoped to this page.
 */
const catalogStyles = `
  .pc-catalog {
    --pc-bg: #ffffff;
    --pc-band: #f6f6f4;
    --pc-tile: #f1f1ef;
    --pc-ink: #16150f;
    --pc-soft: #82807a;
    --pc-line: #e9e8e4;
    background-color: var(--pc-bg);
    color: var(--pc-ink);
    font-family: 'Assistant', 'Rubik', sans-serif;
  }
  .pc-serif { font-family: 'Frank Ruhl Libre', 'Times New Roman', serif; }
  .pc-mono { font-family: 'IBM Plex Mono', monospace; }

  .pc-search {
    background: var(--pc-tile);
    border: 1px solid transparent;
    border-radius: 9999px;
    padding: 0.6rem 2.6rem 0.6rem 1.2rem;
    width: 100%;
    font-size: 0.9rem;
    color: var(--pc-ink);
    transition: background-color 0.25s ease, border-color 0.25s ease;
  }
  .pc-search:focus {
    outline: none;
    background: #fff;
    border-color: var(--pc-ink);
    box-shadow: none;
    --tw-ring-color: transparent;
  }
  .pc-search::placeholder { color: var(--pc-soft); }

  .pc-chip {
    border-radius: 9999px;
    border: 1px solid var(--pc-line);
    background: #fff;
    padding: 0.45rem 1.1rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--pc-ink);
    white-space: nowrap;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  }
  .pc-chip:hover { border-color: var(--pc-ink); }
  .pc-chip[data-active='true'] { background: var(--pc-ink); border-color: var(--pc-ink); color: #fff; }

  .pc-brand-tile {
    border-radius: 1rem;
    overflow: hidden;
    border: 1px solid var(--pc-line);
    background: #fff;
    text-align: center;
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease, border-color 0.3s ease;
  }
  .pc-brand-tile:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px -20px rgba(22, 21, 15, 0.35);
    border-color: var(--pc-ink);
  }
  .pc-brand-tile[data-active='true'] { border-color: var(--pc-ink); box-shadow: 0 0 0 1px var(--pc-ink); }

  .pc-card {
    border-radius: 1rem;
    overflow: hidden;
    background: #fff;
    animation: rcRise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  .pc-card .pc-tile {
    background: var(--pc-tile);
    border-radius: 1rem;
    transition: background-color 0.3s ease;
  }
  .pc-card:hover .pc-tile { background: #eaeae7; }
  .pc-card .pc-card-img { transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1); }
  .pc-card:hover .pc-card-img { transform: scale(1.05); }
  .pc-square { aspect-ratio: 1 / 1; }
  .pc-4x3 { aspect-ratio: 4 / 3; }

  @keyframes rcRise {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes rcSpin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .pc-card { animation: none; }
    .pc-card .pc-card-img, .pc-brand-tile { transition: none; }
  }

  .pc-btn {
    border-radius: 9999px;
    border: 1px solid var(--pc-ink);
    background: #fff;
    color: var(--pc-ink);
    padding: 0.55rem 1.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;
  }
  .pc-btn:hover:not(:disabled) { background: var(--pc-ink); color: #fff; }
  .pc-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  .pc-scroll-x {
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .pc-scroll-x::-webkit-scrollbar { display: none; }
`;

const BrandTile = ({ brand, active, onSelect }) => {
  const productCount = brand.product_count ?? brand.productCount;

  return (
  <button type="button" onClick={onSelect} className="pc-brand-tile block w-full" data-active={active}>
    <div className="pc-4x3 flex items-center justify-center p-6" style={{ backgroundColor: 'var(--pc-tile)' }}>
      {brand.logo ? (
        <img src={brand.logo} alt="" loading="lazy" className="max-h-full max-w-full object-contain mix-blend-multiply" />
      ) : (
        <span className="pc-serif text-4xl font-medium sm:text-5xl">{brand.name.charAt(0)}</span>
      )}
    </div>
    <div className="px-3 py-3.5">
      <div className="truncate text-sm font-bold">{brand.name}</div>
      {typeof productCount === 'number' && (
        <div className="mt-0.5 text-xs" style={{ color: 'var(--pc-soft)' }}>
          {productCount} מוצרים
        </div>
      )}
    </div>
  </button>
  );
};

const ProductCard = ({ product, index, onOpenImage }) => {
  const imageSrc = getImageSrc(product);
  const categoryName = product.category?.name;

  return (
    <article className="pc-card" style={{ animationDelay: `${Math.min(index, 15) * 40}ms` }}>
      <button
        type="button"
        onClick={() => imageSrc && onOpenImage(imageSrc, product.name)}
        disabled={!imageSrc}
        className="pc-tile pc-square relative block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pc-ink)]"
        aria-label={imageSrc ? `פתח תמונה של ${product.name}` : product.name}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            loading="lazy"
            className="pc-card-img absolute inset-0 h-full w-full object-contain p-5 mix-blend-multiply"
          />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center p-8">
            <img src="/RamiCareLogo-01.png" alt="" className="w-2/3 opacity-10 mix-blend-multiply" />
          </span>
        )}
      </button>

      <div className="px-2 pb-4 pt-3 text-center">
        {categoryName && (
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--pc-soft)' }}>
            {categoryName}
          </div>
        )}
        <h3 className="line-clamp-2 text-[0.92rem] font-bold leading-snug" title={product.name}>
          {product.name}
        </h3>
        <div className="pc-mono mt-1.5 text-xs" style={{ color: 'var(--pc-soft)' }} dir="ltr">
          {product.sku || '—'}
        </div>
      </div>
    </article>
  );
};

const PublicCatalogPage = () => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrandKey, setSelectedBrandKey] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: ITEMS_PER_PAGE
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState({ src: null, alt: '' });
  const productsRef = useRef(null);
  const pendingScrollRef = useRef(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 350);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedBrandKey]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCatalog = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams({
          page: String(currentPage),
          limit: String(ITEMS_PER_PAGE)
        });
        const search = debouncedSearchTerm.trim();
        const brandQuery = getBrandQuery(selectedBrandKey);

        if (search) params.set('search', search);
        if (brandQuery.brand_id) params.set('brand_id', brandQuery.brand_id);
        if (brandQuery.brand) params.set('brand', brandQuery.brand);

        const response = await axios.get(`/api/products/public-catalog?${params.toString()}`, {
          signal: controller.signal
        });

        setProducts(response.data.products || []);
        setBrands(response.data.brands || []);
        setPagination(response.data.pagination || {
          currentPage,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: ITEMS_PER_PAGE
        });
        setError('');
      } catch (err) {
        if (err.name === 'CanceledError') return;
        console.error('Error fetching public catalog:', err);
        setError('לא ניתן לטעון את הקטלוג כרגע');
      } finally {
        setLoading(false);
      }
    };

    fetchCatalog();

    return () => controller.abort();
  }, [currentPage, debouncedSearchTerm, selectedBrandKey]);

  const selectedBrand = brands.find((brand) => brand.key === selectedBrandKey);

  const groupedProducts = useMemo(() => {
    const groups = new Map();

    products.forEach((product) => {
      // Only managed brands (with id) get their own section; legacy free-text
      // brand names are not shown — everything else lands in one group.
      const brand = product.brand;
      const isManaged = Boolean(brand?.id);
      const key = isManaged ? `id:${brand.id}` : 'other';

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: isManaged ? brand.name : 'ללא מותג',
          logo: isManaged ? brand.logo : null,
          isManaged,
          products: []
        });
      }

      groups.get(key).products.push(product);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        products: [...group.products].sort(compareSku)
      }))
      .sort((a, b) => {
        if (a.isManaged !== b.isManaged) return a.isManaged ? -1 : 1;
        return a.name.localeCompare(b.name, 'he', { sensitivity: 'base' });
      });
  }, [products]);

  const rangeStart = pagination.totalItems ? (pagination.currentPage - 1) * pagination.itemsPerPage + 1 : 0;
  const rangeEnd = Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems);

  const selectBrand = (brandKey, scroll = true) => {
    setSelectedBrandKey((current) => {
      const next = current === brandKey ? '' : brandKey;
      if (scroll && next) pendingScrollRef.current = true;
      return next;
    });
  };

  useEffect(() => {
    if (!loading && pendingScrollRef.current) {
      pendingScrollRef.current = false;
      productsRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [loading]);

  const openImage = (src, alt) => {
    setLightbox({ src, alt });
  };

  return (
    <div className="pc-catalog min-h-screen" dir="rtl">
      <style>{catalogStyles}</style>

      {/* Sticky header: wordmark + search + brand chips */}
      <header
        className="sticky top-0 z-30 border-b bg-white/95 backdrop-blur"
        style={{ borderColor: 'var(--pc-line)' }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 py-3 sm:gap-8">
            <img
              src="/RamiCareLogo-02.png"
              alt="Rami Care — Beyond Brands"
              className="h-7 shrink-0 object-contain mix-blend-multiply sm:h-9"
            />
            <label className="relative mr-auto block w-full max-w-xs">
              <span className="sr-only">חיפוש מוצרים</span>
              <svg
                className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2"
                style={{ color: 'var(--pc-soft)' }}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="חיפוש לפי שם או מק״ט"
                className="pc-search"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-1 text-lg leading-none"
                  style={{ color: 'var(--pc-soft)' }}
                  aria-label="נקה חיפוש"
                >
                  ×
                </button>
              )}
            </label>
          </div>

          {brands.length > 0 && (
            <nav className="pc-scroll-x -mx-4 flex gap-2 px-4 pb-3 sm:mx-0 sm:px-0" aria-label="סינון מהיר לפי מותג">
              <button
                type="button"
                onClick={() => setSelectedBrandKey('')}
                className="pc-chip"
                data-active={selectedBrandKey === ''}
              >
                כל המותגים
              </button>
              {brands.map((brand) => (
                <button
                  type="button"
                  key={brand.key}
                  onClick={() => selectBrand(brand.key, false)}
                  className="pc-chip"
                  data-active={selectedBrandKey === brand.key}
                >
                  {brand.name}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>

      <main>
        {/* Hero banner */}
        <section className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-2xl px-6 py-12 text-center sm:py-16"
            style={{ backgroundColor: 'var(--pc-band)' }}
          >
            <img
              src="/RamiCareLogo-01.png"
              alt=""
              className="pointer-events-none absolute -left-10 top-1/2 h-64 -translate-y-1/2 opacity-[0.06] mix-blend-multiply sm:h-80"
            />
            <img
              src="/RamiCareLogo-01.png"
              alt=""
              className="pointer-events-none absolute -right-10 top-1/2 h-64 -translate-y-1/2 opacity-[0.06] mix-blend-multiply sm:h-80"
            />
            <h1 className="pc-serif relative text-3xl font-medium sm:text-5xl">קטלוג המוצרים</h1>
            <p className="relative mx-auto mt-4 max-w-xl text-[15px] leading-7" style={{ color: 'var(--pc-soft)' }}>
              כל המותגים והמוצרים שלנו במקום אחד, מסודרים לפי מק״ט.
              בחרו מותג והתחילו לעיין.
            </p>
            <div className="relative mt-6 flex items-center justify-center gap-6 text-sm font-semibold">
              <span>{pagination.totalItems} מוצרים</span>
              <span className="h-4 w-px" style={{ backgroundColor: 'var(--pc-line)' }} aria-hidden="true" />
              <span>{brands.length} מותגים</span>
            </div>
          </div>
        </section>

        {/* Brand tiles — the main way to pick a brand */}
        {brands.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="text-xl font-bold sm:text-2xl">בחרו מותג</h2>
              {selectedBrand && (
                <button type="button" onClick={() => setSelectedBrandKey('')} className="pc-chip">
                  ✕ ניקוי בחירה
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
              {brands.map((brand) => (
                <BrandTile
                  key={brand.key}
                  brand={brand}
                  active={selectedBrandKey === brand.key}
                  onSelect={() => selectBrand(brand.key)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Products */}
        <section ref={productsRef} className="mx-auto max-w-7xl scroll-mt-32 px-4 py-10 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xl font-bold sm:text-2xl">
              {selectedBrand ? selectedBrand.name : 'כל המוצרים'}
            </h2>
            {pagination.totalItems > 0 && (
              <span className="text-sm" style={{ color: 'var(--pc-soft)' }}>
                מציג {rangeStart}–{rangeEnd} מתוך {pagination.totalItems} · מיון לפי מק״ט
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex min-h-96 flex-col items-center justify-center gap-5">
              <div
                className="h-9 w-9 rounded-full border-2"
                style={{
                  borderColor: 'var(--pc-line)',
                  borderTopColor: 'var(--pc-ink)',
                  animation: 'rcSpin 0.8s linear infinite'
                }}
              />
              <p className="text-sm font-semibold" style={{ color: 'var(--pc-soft)' }}>
                טוען קטלוג...
              </p>
            </div>
          ) : error ? (
            <div className="flex min-h-96 flex-col items-center justify-center text-center">
              <p className="text-xl font-bold">{error}</p>
              <button type="button" onClick={() => setCurrentPage(1)} className="pc-btn mt-6">
                נסה שוב
              </button>
            </div>
          ) : groupedProducts.length === 0 ? (
            <div
              className="flex min-h-96 flex-col items-center justify-center rounded-2xl text-center"
              style={{ backgroundColor: 'var(--pc-band)' }}
            >
              <img src="/RamiCareLogo-01.png" alt="" className="h-24 opacity-10 mix-blend-multiply" />
              <h3 className="mt-5 text-xl font-bold">לא נמצאו מוצרים</h3>
              <p className="mt-2 text-sm" style={{ color: 'var(--pc-soft)' }}>
                נסו לשנות את החיפוש או לבחור מותג אחר.
              </p>
            </div>
          ) : selectedBrand || groupedProducts.length === 1 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 xl:grid-cols-5">
              {groupedProducts.flatMap((group) => group.products).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} onOpenImage={openImage} />
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {groupedProducts.map((group) => (
                <section key={group.key}>
                  <div className="mb-4 flex items-baseline justify-between gap-3">
                    <h3 className="text-lg font-bold sm:text-xl">{group.name}</h3>
                    {group.isManaged && (
                      <button type="button" onClick={() => selectBrand(group.key)} className="pc-chip">
                        הצג רק {group.name}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 xl:grid-cols-5">
                    {group.products.map((product, index) => (
                      <ProductCard key={product.id} product={product} index={index} onOpenImage={openImage} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && !loading && (
            <nav className="mt-12 flex items-center justify-center gap-5" aria-label="עמודים">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={pagination.currentPage <= 1}
                className="pc-btn"
              >
                הקודם
              </button>
              <span className="text-sm font-semibold" style={{ color: 'var(--pc-soft)' }}>
                עמוד {pagination.currentPage} מתוך {pagination.totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(pagination.totalPages, page + 1))}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="pc-btn"
              >
                הבא
              </button>
            </nav>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t px-4 py-12 text-center" style={{ borderColor: 'var(--pc-line)' }}>
        <img
          src="/RamiCareLogo-01.png"
          alt="Rami Care — Beyond Brands"
          className="mx-auto h-20 mix-blend-multiply sm:h-24"
        />
        <p className="mt-4 text-xs font-semibold" style={{ color: 'var(--pc-soft)' }}>
          © {new Date().getFullYear()} Rami Care · כל הזכויות שמורות
        </p>
      </footer>

      <ImageLightbox
        src={lightbox.src}
        alt={lightbox.alt}
        isOpen={!!lightbox.src}
        onClose={() => setLightbox({ src: null, alt: '' })}
      />
    </div>
  );
};

export default PublicCatalogPage;
